import React, {useContext} from 'react';
import {Control, Controller} from 'react-hook-form';
import {FieldPath} from 'react-hook-form/dist/types';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import {FormFieldConfigSize, FormFieldOption} from '@src/@types/components/Form';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import CustomSelect from '@src/components/Fields/CustomSelect';
import classnames from '@src/helpers/classnames';
import FormErrorMessage from '@src/components/Form/FormErrorMessage';

export function ConditionalStatusField<Model extends IOrderModel = IOrderModel>(props: {
    control: Control<Model>,
    fieldName: FieldPath<Model>,
    fieldLabel: string,
    options: (FormFieldOption & {
        comment_is_required?: boolean,
        approval_is_required?: boolean,
    })[],
    size?: FormFieldConfigSize,
    forceDisabled?: boolean,
    forceReadonly?: boolean,
    menuPortalTarget?: HTMLElement | null,
    approvalRequired?: boolean,
}) {
    const [design] = useContext(FormFieldDesignContext);
    const {
        control,
        fieldName,
        fieldLabel,
        options,
        forceReadonly,
        forceDisabled,
        size,
        menuPortalTarget,
        approvalRequired,
    } = props;

    return <>
        <Controller
                control={control}
                name={fieldName}
                render={({field: {name, value, onChange, onBlur}, fieldState: {error}}) => {
                    const component = <CustomSelect
                            label={fieldLabel}
                            className={classnames({
                                'is-invalid': Boolean(error),
                                'flex-grow-1': approvalRequired,
                            })}
                            name={name}
                            size={size}
                            options={options}
                            readOnly={design.fieldsAreReadOnly || forceReadonly}
                            disabled={design.fieldsAreDisabled || forceDisabled}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            menuPortalTarget={menuPortalTarget ?? document.body}
                    />;

                    if (!approvalRequired) {
                        return component;
                    }

                    return <div className={classnames('input-group flex-nowrap', {[`input-group-${size}`]: size})}>
                        <span className="input-group-text">
                            <i
                                    className="fa fa-binoculars text-warning with-pulse-animation rounded-1"
                                    style={{'--animation-pulse--color': 'var(--bs-warning-rgb)'} as React.CSSProperties}
                            />
                        </span>

                        {component}
                    </div>;
                }}
        />

        <FormErrorMessage fieldName={fieldName}/>
    </>;
}
